import { IMAGE_EXTENSION_FALLBACKS } from '../types';

export const HEIC_JPEG_QUALITY = 0.92;

const CONVERSION_TIMEOUT_MS = 30_000;

let heicModulePromise = null;

const loadHeicModule = () => {
  if (!heicModulePromise) {
    heicModulePromise = import('heic-to');
  }
  return heicModulePromise;
};

const getFileExtension = (fileName) => {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex === -1) return '';
  return fileName.substring(dotIndex).toLowerCase();
};

const toJpegFileName = (fileName) => {
  const baseName = fileName.replace(/\.(heic|heif)$/i, '');
  return `${baseName}.jpg`;
};

const withTimeout = (promise, timeoutMs, timeoutMessage) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    }),
  ]);

export const hasHeicOrHeifExtension = (fileName = '') =>
  IMAGE_EXTENSION_FALLBACKS.includes(getFileExtension(fileName));

export const shouldConvertHeicToJpeg = (file) => {
  if (!file) return false;
  const mime = (file.type || '').toLowerCase();
  if (mime === 'image/heic' || mime === 'image/heif') return true;
  return hasHeicOrHeifExtension(file.name);
};

const detectHeicContent = async (file) => {
  try {
    const { isHeic } = await loadHeicModule();
    return await isHeic(file);
  } catch {
    return false;
  }
};

/**
 * Convert a HEIC/HEIF file to JPEG for universal preview and WhatsApp delivery.
 * @param {File} file
 * @returns {Promise<File>}
 */
export const convertHeicToJpeg = async (file) => {
  const needsConversion =
    shouldConvertHeicToJpeg(file) || (await detectHeicContent(file));

  if (!needsConversion) {
    throw new Error('File is not a HEIC/HEIF image');
  }

  const { heicTo } = await loadHeicModule();

  const converted = await withTimeout(
    heicTo({
      blob: file,
      type: 'image/jpeg',
      quality: HEIC_JPEG_QUALITY,
    }),
    CONVERSION_TIMEOUT_MS,
    'HEIC conversion timed out'
  );

  const blob = converted instanceof Blob
    ? converted
    : new Blob([converted], { type: 'image/jpeg' });

  if (!blob || blob.size === 0) {
    throw new Error('HEIC conversion produced an empty file');
  }

  return new File([blob], toJpegFileName(file.name), {
    type: 'image/jpeg',
    lastModified: file.lastModified || Date.now(),
  });
};
